import { Link, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, LogIn, XCircle } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useAcceptJoinLink, useJoinLinkInfo } from '@/hooks/use-projects';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { withRedirect } from '@/lib/auth-redirect';

export function JoinPage() {
    const { token = '' } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { language, t } = useI18n();
    const { isAuthenticated } = useAuthStore();
    const { data, isLoading, error } = useJoinLinkInfo(token);
    const acceptJoinLink = useAcceptJoinLink();
    const redirectPath = `/join/${token}`;

    const copy = language === 'fr'
        ? {
            title: 'Vous êtes invité à rejoindre',
            description: 'Toute personne disposant de ce lien peut rejoindre ce projet comme membre.',
            action: 'Rejoindre le projet',
            invalidTitle: 'Lien d’invitation invalide',
            invalidDescription: 'Ce lien a été désactivé ou remplacé. Demandez un nouveau lien à un administrateur du projet.',
        }
        : {
            title: "You're invited to join",
            description: 'Anyone with this link can join this project as a member.',
            action: 'Join project',
            invalidTitle: 'Invalid join link',
            invalidDescription: 'This link was disabled or replaced. Ask a project administrator for a new link.',
        };

    if (isLoading) {
        return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
    }

    if (error || !data) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4">
                <Card className="max-w-md w-full p-6 text-center space-y-4">
                    <XCircle className="h-12 w-12 text-destructive mx-auto" />
                    <h1 className="text-xl font-semibold">{copy.invalidTitle}</h1>
                    <p className="text-sm text-foreground-muted">{copy.invalidDescription}</p>
                    <Button asChild><Link to="/">{t('nav.home')}</Link></Button>
                </Card>
            </div>
        );
    }

    const handleJoin = async () => {
        try {
            const result = await acceptJoinLink.mutateAsync(token);
            navigate(`/projects/${result.projectId}`);
        } catch {
            // The mutation state renders the disabled/invalid link message.
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center p-4">
            <Card className="max-w-md w-full p-6 sm:p-8 text-center">
                {data.project.imageUrl ? (
                    <img src={data.project.imageUrl} alt={data.project.name} className="h-16 w-16 rounded-xl mx-auto mb-4 object-cover" />
                ) : (
                    <div className="h-16 w-16 rounded-xl flex items-center justify-center text-3xl mx-auto mb-4" style={{ backgroundColor: data.project.color || '#6366f1' }}>
                        {data.project.iconEmoji || '📁'}
                    </div>
                )}
                <h1 className="text-xl font-semibold mb-2">{copy.title}</h1>
                <p className="text-2xl font-bold mb-3">{data.project.name}</p>
                <p className="text-sm text-foreground-muted mb-6">{copy.description}</p>
                {!isAuthenticated ? (
                    <div className="space-y-3">
                        <Button asChild className="w-full">
                            <Link to={withRedirect('/login', redirectPath)}><LogIn className="mr-2 h-4 w-4" />{t('auth.signIn')}</Link>
                        </Button>
                        <Button asChild variant="outline" className="w-full">
                            <Link to={withRedirect('/register', redirectPath)}>{t('auth.createAccount')}</Link>
                        </Button>
                    </div>
                ) : (
                    <Button className="w-full" onClick={handleJoin} disabled={acceptJoinLink.isPending}>
                        {acceptJoinLink.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                        {copy.action}
                    </Button>
                )}
                {acceptJoinLink.isError && <p className="text-sm text-destructive mt-3">{copy.invalidDescription}</p>}
            </Card>
        </div>
    );
}
